import React from 'react';
import { Helmet } from 'react-helmet';
import JsonLdList from '../../../common/seo/JsonLdList';
import { SITE_URL, getBreadcrumbSchema, organizationSchema } from '../../../../utils/seo';
import { buildFaqSchema, buildWebPageSchema } from '../../../../utils/schema';
import RouteConstants from '../../../../utils/RouteConstants';
import pageAssets from '../../../../data/pageAssets';
import { buildOfferSchema } from '../../../../data/db';

const PAGE_URL = `${SITE_URL}${RouteConstants.ORBIT_STAR}`;
const IMAGE_URL = `${SITE_URL}${pageAssets.orbitStar}`;
// Social crawlers handle WebP inconsistently, so link previews use the JPG banner.
const SHARE_IMAGE_URL = `${SITE_URL}${pageAssets.orbitStarDiwaliBanner}`;

const OrbitStarSeo = ({ seo, product, specs, variants, faqItems }) => {
    const [mainModel, ...otherModels] = variants.models;
    const mainImages = mainModel.items
        .filter((item) => item.imageKey)
        .map((item) => `${SITE_URL}${pageAssets[item.imageKey]}`);

    const schemas = [
        organizationSchema,
        getBreadcrumbSchema([
            { name: 'Home', url: `${SITE_URL}/` },
            { name: 'Orbit Star LED String Light', url: PAGE_URL }
        ]),
        buildWebPageSchema({
            name: seo.webPageName,
            url: PAGE_URL,
            description: seo.webPageDescription,
            about: [
                { "@type": "Thing", "name": "Christmas lights", "sameAs": "https://www.wikidata.org/wiki/Q1653073" },
                { "@type": "Thing", "name": "Light-emitting diode", "sameAs": "https://www.wikidata.org/wiki/Q25504" }
            ],
            speakable: ['.orbit-star-hero', '.orbit-star-specs', '.orbit-star-faq']
        }),
        {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": product.name,
            "sku": product.sku,
            "mpn": product.sku,
            "category": product.category,
            "description": product.description,
            "image": [IMAGE_URL, ...mainImages],
            "offers": mainModel.price ? buildOfferSchema(String(mainModel.price)) : undefined,
            "color": mainModel.items.map((item) => item.colour).join(', '),
            "sameAs": mainModel.items.map((item) => item.buyUrl).filter(Boolean),
            "url": PAGE_URL,
            "brand": { "@type": "Brand", "name": "NISHAD" },
            "additionalProperty": specs.map((row) => ({
                "@type": "PropertyValue",
                "name": row.label,
                "value": row.value
            }))
        },
        ...otherModels.filter((model) => model.structuredProduct).map((model) => ({
            "@context": "https://schema.org",
            "@type": "ProductGroup",
            "name": model.structuredProduct.name,
            "productGroupID": model.structuredProduct.productGroupId,
            "category": model.structuredProduct.category,
            "description": model.structuredProduct.description,
            "url": `${PAGE_URL}#variants`,
            "brand": { "@type": "Brand", "name": "NISHAD" },
            "variesBy": ["https://schema.org/color"],
            "hasVariant": model.items.map((item) => ({
                "@type": "Product",
                "name": `${model.structuredProduct.name} - ${item.colour}`,
                "sku": item.sku,
                "color": item.colour,
                ...(item.buyUrl && { "sameAs": item.buyUrl }),
                "image": item.imageKey ? `${SITE_URL}${pageAssets[item.imageKey]}` : IMAGE_URL,
                "offers": model.price ? buildOfferSchema(String(model.price)) : undefined
            }))
        })),
        buildFaqSchema(faqItems)
    ];

    return (
        <>
            <Helmet>
                <title>{seo.title}</title>
                <meta name="description" content={seo.description} />
                <meta name="keywords" content={seo.keywords} />
                <meta name="robots" content="index, follow" />
                <meta name="author" content="Nishad Cables" />
                <link rel="canonical" href={PAGE_URL} />

                <meta name="geo.region" content="IN-RJ" />
                <meta name="geo.placename" content="Alwar" />
                <meta name="geo.position" content="27.5530; 76.6346" />
                <meta name="ICBM" content="27.5530, 76.6346" />

                <meta property="og:type" content="product" />
                <meta property="og:title" content={seo.title} />
                <meta property="og:description" content={seo.ogDescription} />
                <meta property="og:image" content={SHARE_IMAGE_URL} />
                <meta property="og:image:alt" content={product.name} />
                <meta property="og:url" content={PAGE_URL} />
                <meta property="og:site_name" content="NISHAD" />
                <meta property="og:locale" content="en_IN" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seo.title} />
                <meta name="twitter:description" content={seo.twitterDescription} />
                <meta name="twitter:image" content={SHARE_IMAGE_URL} />
            </Helmet>
            <JsonLdList items={schemas} />
        </>
    );
};

export default OrbitStarSeo;
